import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import { FaqFilters } from '../components/faq/FaqFilters';
import { FaqList } from '../components/faq/FaqList';
import { FaqPagination } from '../components/faq/FaqPagination';
import { FaqSuggestForm } from '../components/faq/FaqSuggestForm';
import { useRelatedFaqs } from '../components/faq/useRelatedFaqs';
import { useFaqMissLogger } from '../components/faq/useFaqMissLogger';
import { buildIndex, searchFaqs } from '../components/faq/faqSearch';
import { usePageMeta } from '../lib/usePageMeta';
import faqContent from '../content/faq/index.json';
import { HelpCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FILTERS = { query: '', type: null, topic: null, audience: null };
const PAGE_SIZE = Number(faqContent.pageSize) || 10;

const matches = (item, f) => {
  if (f.type && item.type !== f.type) return false;
  if (f.topic && !item.topics.includes(f.topic)) return false;
  if (f.audience && !item.audience.includes(f.audience)) return false;
  return true;
};

const FaqPage = () => {
  usePageMeta('FAQ — ResiAX', faqContent.hero.description);
  const location = useLocation();
  const [data, setData] = useState({ items: [], topics: [], audiences: [], types: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [openSlug, setOpenSlug] = useState('');
  const [suggestSeed, setSuggestSeed] = useState('');
  const listRef = useRef(null);
  const handledHash = useRef('');

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
    fetch(`${API}/api/faq`)
      .then((r) => { if (!r.ok) throw new Error(`Failed to load FAQs (${r.status})`); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const index = useMemo(() => buildIndex(data.items), [data.items]);
  const { matches: searched, fuzzy } = useMemo(() => searchFaqs(index, filters.query), [index, filters.query]);
  const filtered = useMemo(() => searched.filter((i) => matches(i, filters)), [searched, filters]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hasActiveFilters = Boolean(filters.query || filters.type || filters.topic || filters.audience);
  const getRelated = useRelatedFaqs(data.items, Number(faqContent.related?.count) || 3);
  useFaqMissLogger({ ready: !loading && !error && data.items.length > 0, resultCount: filtered.length, filters });

  // /faq?ask=<text> (from the digest) → pre-fill the suggest form and scroll to it
  useEffect(() => {
    const ask = new URLSearchParams(location.search).get('ask');
    if (!ask || loading) return;
    setSuggestSeed(ask.trim());
    setTimeout(() => document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, [location.search, loading]);

  // Jump to any published item: clear filters if it is hidden, switch page, open, scroll
  const goTo = useCallback((slug) => {
    const idx = data.items.findIndex((i) => i.slug === slug);
    if (idx === -1) return;
    setFilters(EMPTY_FILTERS);
    setPage(Math.floor(idx / PAGE_SIZE) + 1);
    setOpenSlug(slug);
    window.history.replaceState(null, '', `#${slug}`);
    setTimeout(() => document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
  }, [data.items]);

  // Deep link: /faq#<slug>
  useEffect(() => {
    const slug = location.hash.replace('#', '');
    if (!slug || loading || handledHash.current === slug) return;
    handledHash.current = slug;
    goTo(slug);
  }, [location.hash, loading, goTo]);

  // FAQPage JSON-LD from all published items
  useEffect(() => {
    if (!data.items.length) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.items.map((i) => ({
        '@type': 'Question',
        name: i.question,
        acceptedAnswer: { '@type': 'Answer', text: i.answer },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [data.items]);

  const updateFilters = (updater) => {
    setFilters(updater);
    setPage(1);
    setOpenSlug('');
  };

  const changePage = (p) => {
    setPage(p);
    setOpenSlug('');
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOpen = (slug) => {
    setOpenSlug(slug);
    window.history.replaceState(null, '', slug ? `#${slug}` : location.pathname);
  };

  const ctaIsInternal = faqContent.stillHaveQuestions.ctaHref?.startsWith('/');

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main className="pt-24 pb-20" data-testid="faq-page">
        <section className="bg-gradient-to-br from-[#1F1E1E] to-[#2D2A2A] text-white py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/20 px-4 py-2 rounded-full mb-6">
              <HelpCircle className="w-5 h-5 text-[#D4722F]" />
              <span className="text-sm font-semibold text-[#D4722F] uppercase tracking-wide">{faqContent.hero.badgeText}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4" data-testid="faq-hero-title">{faqContent.hero.title}</h1>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl">{faqContent.hero.description}</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" ref={listRef}>
          {loading && (
            <div className="flex items-center justify-center py-20" data-testid="faq-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#B5541B]" />
            </div>
          )}
          {error && <p className="text-center text-red-600 py-20" data-testid="faq-error">{error}</p>}

          {!loading && !error && data.items.length === 0 && (
            <div className="text-center py-20 text-[#3B3535]/60" data-testid="faq-empty">
              <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p>{faqContent.emptyState.message}</p>
            </div>
          )}

          {!loading && !error && data.items.length > 0 && (
            <>
              <div className="bg-white/70 backdrop-blur rounded-2xl border border-gray-100 p-4 sm:p-5 mb-8 shadow-sm">
                <FaqFilters
                  content={faqContent.filters}
                  meta={data}
                  filters={filters}
                  setFilters={updateFilters}
                  hasActiveFilters={hasActiveFilters}
                  onClear={() => updateFilters(() => EMPTY_FILTERS)}
                />
              </div>
              <p className="text-sm text-[#3B3535]/60 mb-4" data-testid="faq-result-count">
                {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                {fuzzy && filtered.length > 0 && (
                  <span className="ml-1 text-[#B5541B]" data-testid="faq-fuzzy-note">
                     — {faqContent.filters.fuzzyNote || 'no exact match, showing close matches'}
                  </span>
                )}
              </p>
              {filtered.length === 0 ? (
                <div className="text-center py-16 text-[#3B3535]/60" data-testid="faq-no-match">
                  <p>{faqContent.emptyState.noMatchMessage}</p>
                  {filters.query.trim().length >= 10 && (
                    <button
                      type="button"
                      data-testid="faq-no-match-ask"
                      onClick={() => {
                        setSuggestSeed(filters.query.trim());
                        document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#B5541B] hover:underline"
                    >
                      {faqContent.suggest.askThisLabel} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <FaqList
                    items={pageItems}
                    openSlug={openSlug}
                    onOpenChange={handleOpen}
                    getRelated={getRelated}
                    onGoTo={goTo}
                    relatedHeading={faqContent.related?.heading}
                    helpfulContent={faqContent.helpful}
                    onAskInstead={(item) => {
                      setSuggestSeed(`About "${item.question}": `);
                      document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  />
                  <FaqPagination
                    page={safePage}
                    pageCount={pageCount}
                    onChange={changePage}
                    from={(safePage - 1) * PAGE_SIZE + 1}
                    to={Math.min(safePage * PAGE_SIZE, filtered.length)}
                    total={filtered.length}
                  />
                </>
              )}
            </>
          )}

          <div id="ask" className="mt-16 bg-[#1F1E1E] text-white rounded-2xl p-8 sm:p-10 scroll-mt-28" data-testid="faq-still-have-questions">
            <h2 className="text-lg md:text-xl font-bold mb-2">{faqContent.stillHaveQuestions.heading}</h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mb-6">{faqContent.stillHaveQuestions.description}</p>
            <FaqSuggestForm key={suggestSeed} content={faqContent.suggest} audiences={data.audiences} initialQuestion={suggestSeed} />
            <p className="text-sm text-gray-400 mt-6">
              {faqContent.stillHaveQuestions.contactPrefix}{' '}
              {ctaIsInternal ? (
                <Link to={faqContent.stillHaveQuestions.ctaHref} data-testid="faq-contact-cta" className="font-semibold text-[#D4722F] hover:underline">
                  {faqContent.stillHaveQuestions.ctaLabel}
                </Link>
              ) : (
                <a href={faqContent.stillHaveQuestions.ctaHref} data-testid="faq-contact-cta" className="font-semibold text-[#D4722F] hover:underline">
                  {faqContent.stillHaveQuestions.ctaLabel}
                </a>
              )}
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default FaqPage;
